import { RefObject, useState } from "react";
import {
  Backdrop,
  MenuItem,
  Paper,
  Slide,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { AiChatMessage } from "../../types/AIChat/AiChatMessage";
import { AiChatFeedbackDraft, AiChatFeedbackRating } from "../../types/AIChat/AiChatFeedback";
import {
  AI_CHAT_FEEDBACK_CATEGORY_LABELS,
  createAiChatFeedbackDraft,
  saveAiChatFeedback,
} from "../../services/aiChatFeedbackService";
import { queryKeys } from "../../services/ReactQuery/reactQueryService";
import ButtonLoading from "../Button/ButtonLoading";
import aiChatFeedbackSheetStyles from "./aiChatFeedbackSheetStyles";

type AiChatFeedbackSheetProps = {
  message: AiChatMessage;
  rating: AiChatFeedbackRating;
  activeThreadId: number;
  chatWrapperRef: RefObject<HTMLElement | null>;
  onClose: () => void;
};

export default function AiChatFeedbackSheet({
  message,
  rating,
  activeThreadId,
  chatWrapperRef,
  onClose,
}: AiChatFeedbackSheetProps) {
  const [feedbackDraft, setFeedbackDraft] = useState<AiChatFeedbackDraft>(
    () => createAiChatFeedbackDraft(message, rating)
  );
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const feedbackMutation = useMutation({
    mutationFn: () => saveAiChatFeedback(message.id, feedbackDraft),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.collection("ai_chat_messages", { aiChatThread: activeThreadId }),
      });
      onClose();
    },
    onError: () => {
      enqueueSnackbar("Feedback konnte nicht gespeichert werden. Bitte versuche es erneut.", {
        variant: "error",
      });
    },
  });

  return (
    <>
      <Backdrop
        open
        onClick={feedbackMutation.isPending ? undefined : onClose}
        sx={aiChatFeedbackSheetStyles.backdrop}
      />
      <Slide direction={"up"} in container={chatWrapperRef.current}>
        <Paper sx={aiChatFeedbackSheetStyles.paper}>
          <Stack spacing={2} sx={aiChatFeedbackSheetStyles.content}>
            <Typography variant={"h6"}>Feedback senden</Typography>
            {feedbackDraft.rating === "negative" && (
              <TextField
                select
                fullWidth
                label={"Grund (optional)"}
                value={feedbackDraft.category}
                onChange={(event) =>
                  setFeedbackDraft({
                    ...feedbackDraft,
                    category: event.target.value as AiChatFeedbackDraft["category"],
                  })
                }
              >
                {Object.entries(AI_CHAT_FEEDBACK_CATEGORY_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              fullWidth
              multiline
              minRows={3}
              label={"Bitte Details angeben: (optional)"}
              placeholder={
                feedbackDraft.rating === "positive"
                  ? "Was war zufriedenstellend an dieser Antwort?"
                  : "Was war nicht zufriedenstellend an dieser Antwort?"
              }
              value={feedbackDraft.comment}
              onChange={(event) => setFeedbackDraft({ ...feedbackDraft, comment: event.target.value })}
            />
            <ButtonLoading
              variant={"contained"}
              fullWidth
              isLoading={feedbackMutation.isPending}
              onClick={() => feedbackMutation.mutate()}
            >
              Feedback senden
            </ButtonLoading>
          </Stack>
        </Paper>
      </Slide>
    </>
  );
}
